<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { Button } from '@dashhudson/dashing-ui/button';
import { Chip } from '@dashhudson/dashing-ui/chip';
import { Icon } from '@dashhudson/dashing-ui/icon';
import { Image } from '@dashhudson/dashing-ui/image';
import {
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_PERMISSION_TYPES,
  communitySentimentTypes,
  SENTIMENT_LABEL_BY_TYPE,
} from '@/app/community/constants';
import {
  getAccountHandle,
  getAspectRatioFromUrl,
  getPostSourceLink,
  getPostSourceLinkTooltip,
  getPostThumbnailUrl,
  getStatChips,
  getTargetCaption,
} from '@/app/community/utils/comment-detail';
import VisionAiButton from '@/components/VisionAi/VisionAiButton.vue';
import { useVisionAiPermissions } from '@/components/VisionAi/composables/useVisionAiPermissions';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { useMediaDrawer } from '@/composables/useMediaDrawer';
import AttachmentGroup from '@/app/community/components/AttachmentGroup.vue';
import { attachmentFormatter } from '@/app/community/utils/comments';
import NestedTweetPreview from '@/app/community/components/NestedTweetPreview.vue';
import { portraitMediaTypes } from '@/app/community/utils/formatter';
import { useRoute } from 'vue-router';

const props = defineProps({
  media: { type: Object, default: undefined },
  comment: { type: Object, default: undefined },
  activeComment: { type: Object, default: undefined },
  ugcTweet: { type: Boolean, default: undefined },
  brand: { type: Object, default: undefined },
  clampHeight: { type: Number, default: 0 },
});

const route = useRoute();
const { canAccessYoutubeCompliance, verifyBrandAndUserAccess } = useCommunityPermissions();
const { userHasVisionAiPermissionForCurrentRoute } = useVisionAiPermissions();
const { openMedia } = useMediaDrawer();

const canAccessTextAnalysis = computed(() => {
  const typeCheck = communitySentimentTypes().includes(props.comment?.type);
  const complianceCheck =
    canAccessYoutubeCompliance.value ||
    props.interaction?.type !== COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT;
  const hasAccess = verifyBrandAndUserAccess(
    props.comment?.brandId,
    COMMUNITY_PERMISSION_TYPES.TEXT_ANALYSIS,
  );

  return typeCheck && complianceCheck && hasAccess;
});

const isTiktokMention = computed(
  () => props.comment.type === COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION,
);

/**
 * Post Thumbnail
 */
const aspectRatio = computed(() => getAspectRatioFromUrl(props.media?.imageSizes?.small?.url));
const vertical = computed(
  () =>
    portraitMediaTypes.includes(props.media?.mediaType) ||
    props.comment.type.includes('TIKTOK') ||
    aspectRatio.value < 1,
);
const isTextPost = computed(() => props.media?.sourceData?.type === 'text');
const postThumbnailUrl = computed(() => {
  if (isTiktokMention.value) {
    return props.activeComment?.thumbnailUrl;
  }
  return getPostThumbnailUrl(props.media, vertical.value);
});
const postThumbnailProps = computed(() => {
  if (props.ugcTweet || isTextPost.value) return undefined;

  const baseProps = {
    empty: !!postThumbnailUrl.value,
    src: postThumbnailUrl.value,
    iconSize: 'sm',
    errorIcon: 'missing',
    fit: 'contain',
    class: ['dash-rounded-sm-md', 'min-w-14', 'max-w-14'],
  };
  if (vertical.value) {
    baseProps.class.push('!aspect-[4/5]');
  } else {
    baseProps.class.push('!aspect-square');
  }
  return baseProps;
});

/**
 * Media Info
 */
const headerComment = computed(() => {
  if (props.ugcTweet) {
    return props.comment.topParentStatus;
  }
  return props.media;
});
const mediaAccountHandle = computed(() => {
  if (props.ugcTweet) {
    return props.comment.topParentStatus?.authorUsername;
  }
  if (isTiktokMention.value) {
    return 'TikTok User';
  }
  const handleFromMedia = getAccountHandle(props.media);
  return handleFromMedia ?? props.brand?.name;
});
const sourceLink = computed(() => {
  if (isTiktokMention.value) {
    return props.activeComment?.videoLink;
  }
  if (props.ugcTweet) {
    const { topParentStatus, authorUserId } = props.comment ?? {};
    const tweetId = topParentStatus?.twitterTweetId;
    const userId = topParentStatus?.authorUserId ?? authorUserId;
    if (userId && tweetId) {
      return `https://twitter.com/${userId}/status/${tweetId}`;
    }
    return undefined;
  }
  return getPostSourceLink(props.media, props.comment);
});
const sourceLinkTooltip = computed(() => getPostSourceLinkTooltip(props.comment));
function openMediaDrawer() {
  const { mediaId: id, brandId } = props.comment ?? {};
  if (id && brandId && !props.comment?.isAdComment) {
    openMedia({
      id,
      brandId,
    });
  }
}
function onSourceLinkClick() {
  if (sourceLink.value) {
    window.open(sourceLink.value, '_blank');
  }
}

/**
 * Sentiment
 */
const sentimentChips = computed(() => {
  const { mediaCommentSentiment } = props.comment;
  if (canAccessTextAnalysis.value && mediaCommentSentiment) {
    let total = 0;
    const sentimentStats = [];
    Object.entries(mediaCommentSentiment).forEach(([sentiment, value], index, arr) => {
      const { title, severity } = SENTIMENT_LABEL_BY_TYPE[sentiment];
      let currentValue;
      if (index === arr.length - 1) {
        currentValue = 100 - total;
      } else {
        currentValue = Math.round(value * 100);
        total += currentValue;
      }
      if (currentValue) {
        sentimentStats.push({ badge: { severity }, label: `${currentValue}% ${title}` });
      }
    });
    return sentimentStats;
  }
  return [];
});
const postStatChips = computed(() => {
  const statChips = getStatChips(props.media);
  return [...statChips, ...sentimentChips.value];
});

/**
 * Attachment
 */
const attachments = computed(() => {
  if (props.ugcTweet && headerComment.value?.attachments?.length) {
    return attachmentFormatter(headerComment.value?.attachments);
  }
  return [];
});

/**
 * Nested Preview
 */
const quotedStatus = computed(
  () =>
    /* show nested preview if a quoted status exists:
     * when the selected tweet is a reply on a thread started as
     * quote tweet mentioning the brand
     */
    props.comment?.quotedStatus ?? props.comment?.topParentStatus?.quotedStatus,
);
const nestedPreview = computed(() => {
  if (quotedStatus.value && quotedStatus.value.tweetId !== props.media?.sourceData?.tweetId) {
    return {
      attachments: quotedStatus.value.attachments,
      avatar: quotedStatus.value.profilePictureUrl,
      authorName: quotedStatus.value.screenName,
      text: quotedStatus.value.text,
    };
  }
  return {
    attachments: [],
    avatar: props.brand?.avatarUrl,
    authorName: props.brand?.name,
    text: props.media?.sourceData.text,
  };
});

/**
 * Actions
 */
const showVisionAiButton = computed(
  () =>
    userHasVisionAiPermissionForCurrentRoute.value &&
    !props.ugcTweet &&
    !quotedStatus.value &&
    !isTiktokMention.value &&
    !route.meta?.isSalesforceEmbed,
);

/**
 * Caption
 */
// template ref for caption
const caption = ref(null);

const { width: fetchedWidth } = useElementSize(caption);

const elementWidth = computed(() => fetchedWidth.value);
const captionContentWidth = ref(0);
const viewExpanded = ref(false);
const mounted = ref(false);
const resizeObserver = ref(null);

const captionText = computed(() => {
  if (isTiktokMention.value) {
    return props.activeComment?.caption ?? '';
  }
  if (props.ugcTweet) {
    return props.comment.topParentStatus?.text ?? '';
  }
  return getTargetCaption(props.media) ?? '';
});
const captionIsTruncated = computed(() => captionContentWidth.value >= elementWidth.value);
const resizeLabel = computed(() => (viewExpanded.value ? 'Collapse' : 'Expand'));
const lineClampClass = computed(() => {
  if (captionIsTruncated.value && viewExpanded.value) {
    const { lineHeight } = getComputedStyle(caption.value);
    const lh = parseFloat(lineHeight);

    if (!lh || Number.isNaN(lh)) return 3;
    return Math.floor(props.clampHeight / lh);
  }
  return 1;
});

function getTextContentWidth(el) {
  const text = el.textContent;

  const p = document.createElement('p');
  p.classList.add(...el.classList);
  p.style.whiteSpace = 'nowrap';
  p.style.position = 'absolute';
  p.style.visibility = 'hidden';

  p.textContent = text;
  document.body.appendChild(p);
  const width = p.offsetWidth;
  document.body.removeChild(p);
  return width;
}

onMounted(async () => {
  mounted.value = true;
  await nextTick(); // make sure DOM is rendered
  if (caption.value) {
    captionContentWidth.value = getTextContentWidth(caption.value);
    resizeObserver.value = new ResizeObserver(() => {
      if (mounted.value) {
        captionContentWidth.value = getTextContentWidth(caption.value);
      }
    });
    resizeObserver.value.observe(caption.value);
  }
});

onBeforeUnmount(() => {
  // clean up
  mounted.value = false;
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
  captionContentWidth.value = 0;
});
</script>

<template>
  <div class="dash-flex dash-flex-col dash-gap-4 dash-text-base dash-w-full">
    <div class="dash-flex dash-gap-2 dash-w-full dash-items-start">
      <div
        v-if="postThumbnailProps"
        class="post-thumbnail dash-flex-shrink-0 dash-cursor-pointer dash-overflow-hidden"
        :style="{
          width: `${postThumbnailProps.width}px`,
          height: `${postThumbnailProps.height}px`,
        }"
        @click="openMediaDrawer"
      >
        <Image
          v-dash-tooltip="
            comment?.isAdComment ? 'Post insights not available for ad comments' : undefined
          "
          v-bind="postThumbnailProps"
          :class="{ 'dash-cursor-default': comment?.isAdComment }"
        />
      </div>
      <div class="media-info dash-flex dash-flex-col dash-gap-1 dash-flex-grow">
        <div class="dash-flex dash-gap-1 dash-items-center">
          <Icon
            v-if="comment?.isAdComment"
            name="ad-small"
            :size="12"
            class="dash-bg-background-utility dash-rounded-full dash-fill-icon-subtle !p-0.5"
          />
          <span class="post-author-name dash-text-primary dash-typo-base-medium">
            {{ mediaAccountHandle }}
          </span>
          <Button
            v-if="sourceLink"
            v-dash-tooltip="sourceLinkTooltip"
            icon-only
            icon="link-external"
            size="xs"
            severity="tertiary"
            @click="onSourceLinkClick"
          />
        </div>
        <div v-if="captionText" class="caption dash-flex dash-flex-col dash-gap-1">
          <div
            ref="caption"
            class="dash-text-primary"
            :style="{
              display: '-webkit-box',
              '-webkit-box-orient': 'vertical',
              '-webkit-line-clamp': lineClampClass,
              overflow: 'hidden',
            }"
          >
            <p>{{ captionText }}</p>
          </div>
          <span
            v-show="captionIsTruncated"
            class="dash-text-subtle dash-cursor-pointer"
            data-test="resize-button"
            @click="viewExpanded = !viewExpanded"
          >
            {{ resizeLabel }}
          </span>
        </div>
        <AttachmentGroup :attachments="attachments" />
      </div>
      <VisionAiButton v-if="showVisionAiButton" />
    </div>
    <div v-if="quotedStatus" class="quote-preview">
      <NestedTweetPreview v-bind="nestedPreview" />
    </div>
    <div v-if="postStatChips?.length" class="post-stats dash-flex dash-gap-1">
      <Chip v-for="chipProps in postStatChips" :key="chipProps.label" v-bind="chipProps" />
    </div>
  </div>
</template>
